import { DatePicker, TimePicker } from 'antd';
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
import { DateTime } from 'luxon';
import React, { FunctionComponent, useMemo } from 'react';
import { Noop } from 'react-hook-form';

/**
 * FormDateTimePicker props
 */
interface Props {
  value?: Date;
  onChange: (date: Date) => void;
  onBlur: Noop;
  disabled?: boolean;
  dateOnly?: boolean;
}

/**
 * FormDateTimePicker Component
 */
const FormDateTimePicker: FunctionComponent<Props> = ({
  value,
  onChange,
  onBlur,
  disabled,
  dateOnly,
}) => {
  const dateFormat = 'yyyy-LL-dd';
  const timeFormat = 'hh:mm:ss';

  const dateInstance = useMemo(
    () => (value ? new Date(value) : new Date()),
    [value],
  );
  const valid = useMemo(
    () => DateTime.fromJSDate(dateInstance).isValid,
    [dateInstance],
  );
  const cDate = useMemo(
    () => DateTime.fromJSDate(dateInstance).toFormat(dateFormat),
    [dateInstance],
  );
  const cTime = useMemo(() => {
    let time = DateTime.fromJSDate(dateInstance);
    if (dateOnly) {
      time = time.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    }
    return time.toFormat(timeFormat);
  }, [dateInstance, dateOnly]);
  return (
    <div className="flex-row flex gap-2">
      <DatePicker
        disabled={disabled}
        className="flex-1"
        showTime={false}
        value={value && valid ? dayjs(dateInstance) : undefined}
        onBlur={onBlur}
        onChange={(_data, dateString) => {
          const data = DateTime.fromFormat(
            `${dateString} ${cTime}`,
            `${dateFormat} ${timeFormat}`,
          );
          onChange(data.toJSDate());
        }}
      />
      {!dateOnly ? (
        <TimePicker
          disabled={disabled}
          className="flex-1"
          value={value && valid ? dayjs(dateInstance) : undefined}
          onBlur={onBlur}
          onChange={(_data, dateString) => {
            const data = DateTime.fromFormat(
              `${cDate} ${dateString}`,
              `${dateFormat} ${timeFormat}`,
            );
            onChange(data.toJSDate());
          }}
        />
      ) : null}
    </div>
  );
};

FormDateTimePicker.defaultProps = {
  value: undefined,
};

export default FormDateTimePicker;
